import { useEffect, useRef, type MutableRefObject, type ReactNode } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { SpinnerIcon } from "@/lib/icons";
import { Skeleton } from "@/components/ui/skeleton";
import { useColumnTasks } from "@/hooks/useColumnTasks";
import type { TaskFilterParams } from "@/hooks/useTasks";
import type { TaskExtended } from "@/types";
import type { TaskCompletionToggle } from "@/hooks/useTaskCompletionToggle";
import { KanbanTaskCard } from "./KanbanTaskCard";
import type { KanbanColumnDef } from "@/lib/taskBoardMeta";

// Kanban column with per-column pagination and virtual rendering.
// refetchRef is set to the column's refetch function so the parent can
// trigger a full reset (e.g. after a task mutation).
export function KanbanColumn({
  column,
  filterParams,
  projectNameById,
  plotNameById,
  onTaskClick,
  onToggleCompleted,
  refetchRef,
  topSlot,
}: {
  column: KanbanColumnDef;
  filterParams: TaskFilterParams;
  projectNameById: Map<string, string>;
  plotNameById: Map<string, string>;
  onTaskClick: (task: TaskExtended) => void;
  onToggleCompleted?: TaskCompletionToggle;
  refetchRef?: MutableRefObject<(() => void) | null>;
  topSlot?: ReactNode;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  // When an external status filter has pruned this column's status set down
  // to nothing, skip the query entirely and render an empty column. Sending
  // statuses=[] to the backend would otherwise match no status constraint
  // and return everything.
  const skipQuery = column.statuses !== undefined && column.statuses.length === 0;
  const result = useColumnTasks(column.status, filterParams, skipQuery, column.statuses);
  // Apollo's `skip` does not clear the previously returned data, so force
  // empty values when we intend to skip. Otherwise stale tasks can linger
  // when an external filter (e.g. the "Odottaa aliurakoitsijaa" preset) prunes this
  // column's status set between renders.
  const tasks = skipQuery ? [] : result.tasks;
  const totalCount = skipQuery ? 0 : result.totalCount;
  const hasNextPage = skipQuery ? false : result.hasNextPage;
  const loading = skipQuery ? false : result.loading;
  const isFetchingMore = skipQuery ? false : result.isFetchingMore;
  const { fetchNextPage, refetch } = result;

  // Skeletons belong to the first load only. A refetch (status toggle,
  // subscription event) keeps the column's current content on screen instead
  // of blanking it — otherwise an empty column flashes grey placeholders
  // every time a card is about to arrive in it.
  const hasLoadedRef = useRef(false);
  useEffect(() => {
    if (!loading && !skipQuery) hasLoadedRef.current = true;
  }, [loading, skipQuery]);
  const showSkeletons = loading && !hasLoadedRef.current;

  // Expose refetch to the parent via the ref.
  useEffect(() => {
    if (refetchRef) {
      refetchRef.current = refetch;
    }
  }, [refetchRef, refetch]);

  const virtualizer = useVirtualizer({
    count: tasks.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 160,
    overscan: 10,
    // Key the size cache by task id, not by index. Without this, changing
    // the sort order reuses the previous item's cached height for the
    // task that now occupies that index — which causes cards to overlap.
    getItemKey: (index) => tasks[index]?.id ?? index,
  });

  const virtualItems = virtualizer.getVirtualItems();

  // Start fetching the next page when the user is within 8 items of the end
  // so new content is ready before they reach the last loaded item.
  useEffect(() => {
    const lastItem = virtualItems.at(-1);
    if (!lastItem) return;
    if (lastItem.index >= tasks.length - 8 && hasNextPage && !isFetchingMore) {
      fetchNextPage();
    }
  }, [virtualItems, tasks.length, hasNextPage, isFetchingMore, fetchNextPage]);

  return (
    <div className="flex flex-col min-h-0">
      {topSlot && <div className="shrink-0 mb-3 pr-2 max-h-[50%] overflow-y-auto empty:hidden">{topSlot}</div>}
      <div className="flex items-center gap-2 mb-3 shrink-0">
        <div className={`h-3 w-3 rounded-full ${column.color}`} />
        <h2 className="text-sm font-semibold">{column.label}</h2>
        <span className="text-xs text-muted-foreground">({totalCount})</span>
      </div>
      <div ref={scrollRef} className="flex-1 overflow-y-auto min-h-0 pr-2">
        {showSkeletons ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-32 w-full" />
            ))}
          </div>
        ) : tasks.length === 0 ? (
          <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
            Ei tehtäviä
          </div>
        ) : (
          <div style={{ height: virtualizer.getTotalSize(), position: "relative" }}>
            {virtualItems.map((virtualItem) => (
              <KanbanTaskCard
                key={tasks[virtualItem.index].id}
                task={tasks[virtualItem.index]}
                projectNameById={projectNameById}
                plotNameById={plotNameById}
                onClick={() => onTaskClick(tasks[virtualItem.index])}
                onToggleCompleted={onToggleCompleted}
                measureRef={virtualizer.measureElement}
                virtualItem={virtualItem}
              />
            ))}
          </div>
        )}
        {isFetchingMore && (
          <div className="flex justify-center py-2">
            <SpinnerIcon className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}
      </div>
    </div>
  );
}
